import type { Action } from '@reduxjs/toolkit';

import type { NavigationLocationState } from '../../Navigation/types';
import type { ReduxAction, ThunkDispatchType } from 'app/redux/types';

import { API, getAsyncThunk } from 'app/api';
import { addBetFactory } from 'app/modules/BetSlip/actions/addBetFactory';
import {
  MATCHES_WITH_FAVOURITES,
  MATCHES_WITH_MY_STAKES,
  PREMATCH_REQUEST_PAYLOAD_FIELD_CATEGORIES,
  PREMATCH_REQUEST_PAYLOAD_FIELDS,
} from 'app/modules/Prematch/constants';
import { matches, objects, sports } from 'app/modules/Prematch/selectors/gridEntities';

export const addBet = addBetFactory(matches, sports, objects);
export const fetchPrematchCategories = getAsyncThunk(
  'MATCHES:FETCH_PREMATCH_CATEGORIES',
  API.matches.getPrematchCategories,
);
export const CLEANUP_PREMATCH_CONTENT = 'PREMATCH:CLEANUP_CONTENT';

interface Query {
  id?: number | null;
  category?: string;
  favoriteMatches?: string;
  withMyStakes?: string;
  maxDate?: string;
}

export type QueryLocation = Partial<Omit<NavigationLocationState, 'path' | 'list'>>;

export type QueryPayload = {
  dateTimeKey?: string | null;
  filterKey?: string | null;
  signature: string;
} & QueryLocation;

export const buildPrematchContentRequestQuery = (payload: QueryPayload): Query => {
  const query: Query = {};

  for (let i = 0; i < PREMATCH_REQUEST_PAYLOAD_FIELDS.length; i++) {
    const key = PREMATCH_REQUEST_PAYLOAD_FIELDS[i];

    if (payload[key as keyof QueryLocation]) {
      query.category =
        PREMATCH_REQUEST_PAYLOAD_FIELD_CATEGORIES[key as keyof QueryLocation];

      query.id = payload[key as keyof QueryLocation];
      break;
    }
  }

  switch (payload.filterKey) {
    case null:
      break;

    case MATCHES_WITH_FAVOURITES: {
      query.favoriteMatches = 'true';
      break;
    }

    case MATCHES_WITH_MY_STAKES: {
      query.withMyStakes = 'true';
      break;
    }
  }

  if (payload?.dateTimeKey) {
    const maxDate = new Date();
    maxDate.setHours(maxDate.getHours() + Number(payload.dateTimeKey));
    query.maxDate = maxDate.toISOString();
  }

  return query;
};

export const getPrematchContent = getAsyncThunk(
  'MATCHES:FETCH_PREMATCH_CONTENT',
  API.matches.getPrematchContent,
);

export const fetchPrematchContent = (payload: QueryPayload) => {
  return (dispatch: ThunkDispatchType) => {
    dispatch(getPrematchContent({
      signature: payload.signature,
      query: buildPrematchContentRequestQuery(payload),
    }));
  };
};

export const cleanupPrematchContent = (): Action<string> => ({
  type: CLEANUP_PREMATCH_CONTENT,
});

export const SET_MATCHES_CONTAINER_HEIGHT = 'SET_MATCHES_CONTAINER_HEIGHT';

export type SetMatchesContainerHeightType = ReduxAction<
  typeof SET_MATCHES_CONTAINER_HEIGHT, { height: null | number }>;

export const setMatchesContainerHeight = (
  height: null | number,
): SetMatchesContainerHeightType => ({
  type: SET_MATCHES_CONTAINER_HEIGHT,
  payload: { height },
});
